import React, { useState, useMemo } from 'react';
import { Search, X, User, FileText, ChevronDown, ChevronUp } from 'lucide-react';

const GlazeCombinationFinder = () => {
  const [searchInput, setSearchInput] = useState('');
  const [searchMode, setSearchMode] = useState('exact');
  const [selectedImage, setSelectedImage] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});

  // 원본 유약 리스트
  const originalGlazes = useMemo(() => {
    const glazeList = [
      { num: 1, name: '투명유', file: '1_transparent.jpg' },
      { num: 2, name: '보리색유', file: '2_barley.png' },
      { num: 3, name: '단감색유', file: '3_persimmon.png' },
      { num: 4, name: '갈색유', file: '4_brown.png' },
      { num: 5, name: '밤색유', file: '5_chestnut.png' },
      { num: 6, name: '공작색유', file: '6_peacock.png' },
      { num: 7, name: '흑유', file: '7_black.jpg' },
      { num: 9, name: '비취색유', file: '9_jade.png' },
      { num: 10, name: '녹색유', file: '10_green.png' },
      { num: 11, name: '나뭇잎유', file: '11_leaf.jpg' },
      { num: 12, name: '청색유', file: '12_blue.jpg' },
      { num: 13, name: '백매트', file: '13_white_matte.png' },
      { num: 14, name: '상아색유', file: '14_ivory.jpg' },
      { num: 15, name: '황금결정유', file: '15_gold_crystal.jpg' },
      { num: 18, name: '라일락유', file: '18_lilac.jpg' },
      { num: 19, name: '은하수유', file: '19_galaxy.png' },
      { num: 20, name: '눈꽃유', file: '20_snowflake.png' },
      { num: 21, name: '풀잎색유', file: '21_grass.png' },
      { num: 22, name: '완두색유', file: '22_pea.png' },
      { num: 23, name: '연갈색유', file: '23_light_brown.png' },
      { num: 24, name: '옥색유', file: '24_jade_green.jpg' },
      { num: 25, name: '잿빛하늘유', file: '25_ash_sky.png' },
      { num: 26, name: '깊은바다유', file: '26_deep_sea.png' },
      { num: 28, name: '청동유', file: '28_bronze.png' },
      { num: 29, name: '무광투명유', file: '29_matte_clear.jpeg' },
      { num: 30, name: '유백유', file: '30_milky.png' }
    ];
    
    return glazeList.sort((a, b) => a.num - b.num);
  }, []);

  const images = useMemo(() => {
    const allImages = [];
    
    // 실제 존재하는 이미지 조합 목록 (순서 유지)
    const imageCombinations = {
      'A': ['24-14','24-20','23-20','3-20','23-13','14-9','1-23','24-1','1-10','11-1','21-10','9-13','20-11','23-10','3-23','22-24','20-9','21-9','13-10','11-13','20-10','22-3','9-23','10-13','10-20','10-21','1-9','10-24','24-9','1-22','22-9','10-3','9-1','11-14','24-11'],
      'B': ['23-24','20-24','22-1','9-20','13-9','13-24','14-24','22-10','11-21','23-21','23-22','24-23','14-10','9-21','10-14','24-22','14-11','23-3','22-11','20-26','22-2','13-11','11-20','23-11','23-9','2-10','10-2','2-23','3-22','24-26','21-26','9-22','26-13','13-19','22-19'],
      'C': ['21-24','22-21','22-13','14-1','2-1','21-20','20-1','21-11','22-20','13-23','20-22','1-13','20-2','1-2','2-22','3-13','21-22','20-23','3-21','22-14','6-13','22-23','23-25','23-2','14-23','13-6','6-21','13-4','10-15','23-5','6-23','20-6','15-22','4-21','4-23'],
      'D': ['21-14','20-13','2-13','13-2','20-21','1-14','14-3','20-14','13-20','3-14','14-22','2-21','21-2','14-20','21-6','2-20','20-3','13-3','4-14','6-20','15-1','21-4','15-13','5-13','4-20','21-5','15-14','6-14','22-6','15-2','23-6','6-22','15-23','20-5','6-15'],
      'E': ['2-14','14-2','21-7','22-4','4-22','14-26','26-23','13-22','21-3','26-15','15-26','15-25','26-18','25-22','15-21','21-15','12-15','23-7','5-18','22-15','19-20','15-24','15-9','20-7','13-7','23-28','7-15','15-19','15-11','7-22','26-14','26-22','18-4','21-28','26-4'],
      'F': ['15-18','18-23','23-18','21-19','18-9','25-9','14-25','22-25','23-12','13-18','12-20','12-13','20-12','12-21','18-26','2-9','13-12','18-21','18-14','22-12','25-18','9-6','20-19','3-9','10-18','6-9','9-18','18-22','9-5','4-9','25-24','3-25','25-26','18-6','12-9'],
      'G': ['21-19','18-9','25-9','14-25','25-21','21-25','14-12','12-20','12-13','20-12','12-21','12-14','18-25','12-22','18-21','18-14','22-12','25-18','25-1','24-25','10-12','10-18','6-9','9-18','18-22','2-25','26-25','6-26','3-25','25-26','18-6','12-9','12-23','9-12','11-12'],
      'H': ['10-23','11-10','26-10','26-1','26-24','11-23','11-9','10-22','26-21','19-23','19-21','19-20','23-19','14-19','3-26','10-19','19-13','19-14','26-3','23-4','25-23','2-26','19-2','10-25','25-10','19-10','26-5','19-11'],
      'I': ['9-25','9-3','9-24','10-11','11-24','26-20','22-26','9-26','9-10','10-26','10-9','26-2','11-2','11-3','11-22','26-9','1-26','11-25','24-19','11-26','26-11','25-11','26-6','4-26','25-4','2-19','3-19','5-19'],
      'J': ['23-26','2-11','3-11','9-15','15-10','24-15','2-15','9-2','18-15','11-15','22-5','5-15','15-5','7-14','11-19','10-6','11-5','4-10','10-4','3-15','4-15','5-26','11-6','24-6','6-10','6-11','11-4','7-1'],
      'K': ['3-2','6-24','15-20','15-3','14-5','14-4','5-23','18-15','7-21','7-20','23-15','5-14','20-4','1-15','5-21','15-4','6-3','6-4','5-6','9-4','10-5','7-4','4-11','6-5','4-5','4-6','7-3','5-22'],
      'L': ['7-13','7-23','6-19','9-11','19-22','7-9','25-6','6-2','24-5','7-26','18-5','19-6','19-5','19-3','4-3','2-4','19-4','7-10','4-19','7-2','7-11','3-5','3-6','7-19','6-7','18-7','12-7','7-24'],
      'M': ['9-19','19-9','1-19','18-11','19-24','6-12','25-2','19-1','26-19','19-12','12-6','25-19','12-11','19-25','2-7','19-26','26-7','15-7','7-18','7-6','7-25','19-7','3-28','10-28','7-12','7-5','9-28'],
      'N': ['18-11','19-24','6-12','25-2','26-12','6-25','5-25','12-6','25-19','12-11','19-25','12-26','19-18','12-5','15-7','7-18','7-6','7-25','9-7','4-12','11-7','10-28','7-12','7-5','9-28','12-28','5-7','28-3']
    };
    
    Object.keys(imageCombinations).forEach(section => {
      imageCombinations[section].forEach(combination => {
        const parts = combination.split('-');
        allImages.push({
          section,
          combination,
          first: parts[0],
          second: parts[1],
          url: `/${section}_tiles/${section}_${combination}.jpg`,
          fileName: `${section}_${combination}.jpg`
        });
      });
    });
    
    return allImages;
  }, []);

  const filteredImages = useMemo(() => {
    if (!searchInput.trim()) return images;

    const searchTerms = searchInput
      .split(',')
      .map(term => term.trim())
      .filter(Boolean);
    
    if (searchTerms.length === 0) return images;

    return images.filter(img => {
    if (searchMode === 'exact') {
      if (searchTerms.length === 1) {
        const term = searchTerms[0];
        return img.first === term || img.second === term;
      } else {
        // 2개 이상: 조합의 양쪽 번호가 모두 검색어에 포함되어야 함
        return searchTerms.includes(img.first) && searchTerms.includes(img.second);
      }
    } else {
      // 일부 일치
      return searchTerms.some(term => 
        img.first === term || img.second === term
      );
    }
    });
  }, [searchInput, searchMode, images]);

  const groupedImages = useMemo(() => {
    const grouped = {};
    filteredImages.forEach(img => {
      if (!grouped[img.section]) {
        grouped[img.section] = [];
      }
      grouped[img.section].push(img);
    });
    return grouped;
  }, [filteredImages]);

  const sections = Object.keys(groupedImages).sort();

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleComingSoon = (feature) => {
    alert(`"${feature}" 기능은 준비중입니다.`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-blue-800">
                중앙도재 이중시유 검색
              </h1>
              <p className="text-sm text-gray-500 mt-1">Greda Lab - 도예가를 위한 플랫폼</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleComingSoon('내 유약 저장')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <User size={20} />
                <span className="hidden sm:inline">내 유약</span>
              </button>
              <button
                onClick={() => handleComingSoon('유약 메모')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <FileText size={20} />
                <span className="hidden sm:inline">메모</span>
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="유약 번호 입력 (예: 3,15)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-10 py-2 md:py-3 border-2 border-gray-300 rounded-lg focus:border-sky-800 focus:outline-none"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              )}
            </div>

            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="searchMode"
                  value="exact"
                  checked={searchMode === 'exact'}
                  onChange={(e) => setSearchMode(e.target.value)}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">정확 일치</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="searchMode"
                  value="partial"
                  checked={searchMode === 'partial'}
                  onChange={(e) => setSearchMode(e.target.value)}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">일부 일치</span>
              </label>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-900">
                <strong>검색 예시:</strong>
                <span className="ml-2">
                  <kbd className="px-2 py-0.5 bg-white border rounded">3,15</kbd> 정확 일치 → <strong>3-15, 15-3</strong> | 일부 일치 → <strong>3-15, 15-3, 3-10, 15-11 등</strong>
                </span>
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* 원본 유약 리스트 - 스티키 (헤더 바로 아래) */}
        <div className="sticky top-[230px] md:top-[220px] z-40 bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 -mx-4 px-4 pb-6">
          <section>
            <div className="bg-white rounded-lg shadow-md p-4 overflow-x-auto">
              <div className="flex gap-4 pb-2">
                {originalGlazes.map((glaze) => (
                  <div
                    key={glaze.num}
                    className="flex-shrink-0 w-20 md:w-32 cursor-pointer group"
                    onClick={() => setSelectedImage({ 
                      url: `/original_glazes/${glaze.file}`,
                      combination: `${glaze.num}. ${glaze.name}`,
                      first: glaze.num,
                      second: '',
                      section: '원본',
                      isOriginal: true
                    })}
                  >
                    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-2 shadow-sm group-hover:shadow-lg transition-shadow">
                      <img
                        src={`/original_glazes/${glaze.file}`}
                        alt={glaze.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        onError={(e) => {
                          e.target.src = `https://placehold.co/300x300/ddd/666?text=${glaze.num}`;
                        }}
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-xs md:text-sm font-bold text-gray-800">{glaze.num}. {glaze.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>

        {/* 조합 검색 결과 */}
        {sections.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-500 text-lg">검색 결과가 없습니다.</p>
            <p className="text-gray-400 text-sm mt-2">다른 유약 번호를 입력해보세요.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {sections.map(section => (
              <section key={section} className="bg-white rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => toggleSection(section)}
                  className="w-full flex items-center justify-between p-2 md:p-4 bg-sky-800 hover:bg-sky-600 transition-colors"
                >
                  <div className="text-left">
                    <h2 className="text-base md:text-xl font-bold text-white">구역 {section} <span className="text-sm md:text-base font-normal text-sky-100">({groupedImages[section].length}개 조합)</span></h2>
                  </div>
                  {collapsedSections[section] ? (
                    <ChevronDown className="text-white" size={20} />
                  ) : (
                    <ChevronUp className="text-white" size={20} />
                  )}
                </button>

                {!collapsedSections[section] && (
                  <div className="p-4">
                    <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3 md:gap-4">
                      {groupedImages[section].map((img, idx) => (
                        <div
                          key={idx}
                          className="bg-gray-50 rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                          onClick={() => setSelectedImage(img)}
                        >
                          <div className="aspect-square relative bg-gray-100">
                            <img
                              src={img.url}
                              alt={`유약 조합 ${img.combination}`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                              onError={(e) => {
                                e.target.src = `https://placehold.co/400x400/e9967a/white?text=${img.combination}`;
                              }}
                            />
                          </div>
                          <div className="p-2 text-center">
                            <div className="text-gray-800 font-bold text-sm">{img.combination}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </main>

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X size={32} />
            </button>
            <img
              src={selectedImage.url}
              alt={`유약 조합 ${selectedImage.combination}`}
              className="w-full h-auto rounded-lg shadow-2xl"
              onError={(e) => {
                e.target.src = `https://placehold.co/800x800/e9967a/white?text=${selectedImage.combination}`;
              }}
            />
            <div className="bg-white mt-4 p-6 rounded-lg shadow-lg">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-sky-800 rounded-lg flex items-center justify-center">
                  <span className="text-xl font-bold text-white">{selectedImage.section}</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-800">
                  {selectedImage.isOriginal ? selectedImage.combination : `조합: ${selectedImage.combination}`}
                </h3>
              </div>
              {!selectedImage.isOriginal && (
                <p className="text-gray-600 text-lg">
                  {selectedImage.first}번 유약 2회 + {selectedImage.second}번 유약 2회
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <footer className="bg-white border-t mt-12 py-6">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-600 text-sm">
          <p>Greda Lab - 그레다 랩: 도예가를 위한 플랫폼</p>
          <p>© 2025 Greda Lab. All rights reserved.</p>
          <p className="mt-2">
            <a href="https://joongangceramic.com/49" target="_blank" rel="noopener noreferrer" className="text-sky-800 hover:text-sky-600 underline">
              중앙도재 홈페이지 바로가기
            </a>
          </p>
          <p className="mt-2">
            문의하기: <a href="mailto:realnanhea@gmail.com" className="text-sky-800 hover:text-sky-600 underline">realnanhea@gmail.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default GlazeCombinationFinder;